import { useSyncExternalStore } from 'react';

// Persistent global store for Super Admin org/cohort filters.
// Uses sessionStorage so selection survives navigation but not browser close.

function createPersistedStore(key: string, defaultValue = 'all') {
  const read = (): string => {
    try {
      return sessionStorage.getItem(key) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  };

  let current = read();
  const listeners = new Set<() => void>();

  const set = (v: string) => {
    current = v;
    try {
      if (v === defaultValue) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, v);
    } catch {
      /* ignore */
    }
    listeners.forEach((l) => l());
  };

  const subscribe = (cb: () => void) => {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  };

  const getSnapshot = () => current;
  const getServerSnapshot = () => defaultValue;

  const clear = () => set(defaultValue);

  return { set, subscribe, getSnapshot, getServerSnapshot, clear };
}

const orgStore = createPersistedStore('lider:superadmin:selectedOrg');
const cohortStore = createPersistedStore('lider:superadmin:selectedCohort');

export function useOrgFilterStore(): [string, (v: string) => void] {
  const value = useSyncExternalStore(orgStore.subscribe, orgStore.getSnapshot, orgStore.getServerSnapshot);
  return [value, orgStore.set];
}

export function useCohortFilterStore(): [string, (v: string) => void] {
  const value = useSyncExternalStore(
    cohortStore.subscribe,
    cohortStore.getSnapshot,
    cohortStore.getServerSnapshot,
  );
  return [value, cohortStore.set];
}

export function clearSuperAdminFilters() {
  orgStore.clear();
  cohortStore.clear();
}
